import { ChangeDetectorRef, Component, Injector, Input, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { LANGUAGES, LOCALE_LOCALIZATION_KEYS_MAP } from '@modeso/aumico-lib-core-fe';
import { I18NextService } from 'angular-i18next';
import Debug from 'debug';
const debug = Debug('aumico:IntroLayout');

@Component({
    selector: 'aumico-intro-layout',
    templateUrl: './intro-layout.component.html',
    styleUrls: ['./intro-layout.component.scss'],
})
export class IntroLayout implements OnInit {
    @Input() showBackButton: boolean = true; // Input to control back button visibility

    protected readonly router: Router;
    protected readonly localizedTexts: I18NextService;
    private readonly cdr: ChangeDetectorRef;

    // Available languages for the switch
    availableLanguages = [
        {
            code: LANGUAGES.SWISS_GERMAN,
            key: LOCALE_LOCALIZATION_KEYS_MAP.get(LANGUAGES.SWISS_GERMAN) || 'Deutsch (Schweiz)',
        },
        {
            code: LANGUAGES.GERMANY_GERMAN,
            key: LOCALE_LOCALIZATION_KEYS_MAP.get(LANGUAGES.GERMANY_GERMAN) || 'Deutsch (Deutschland)',
        },
        { code: LANGUAGES.ENGLISH, key: LOCALE_LOCALIZATION_KEYS_MAP.get(LANGUAGES.ENGLISH) || 'English (US)' },
        {
            code: LANGUAGES.SWISS_FRENCH,
            key: LOCALE_LOCALIZATION_KEYS_MAP.get(LANGUAGES.SWISS_FRENCH) || 'Français (Suisse)',
        },
    ];

    currentLanguage: string = LANGUAGES.SWISS_GERMAN;

    constructor(injector: Injector) {
        this.router = injector.get(Router);
        this.localizedTexts = injector.get(I18NextService);
        this.cdr = injector.get(ChangeDetectorRef);
    }

    ngOnInit() {
        // Initialize language detection
        this.initializeLanguage();
    }

    /**
     * Initialize and set the current language based on URL
     */
    private initializeLanguage(): void {
        // Get current language from URL first, then fall back to i18next, then default
        const urlLanguage = this.getCurrentLanguageFromUrl();
        const i18nextLanguage = this.localizedTexts.language;
        const detectedLanguage = urlLanguage || i18nextLanguage || LANGUAGES.SWISS_GERMAN;

        debug('Language detection:', {
            currentUrl: this.router.url,
            urlLanguage,
            i18nextLanguage,
            detectedLanguage,
            availableLanguages: this.availableLanguages.map((l) => l.code),
        });

        // Validate that the detected language is in available languages
        const isValidLanguage = this.availableLanguages.some((lang) => lang.code === detectedLanguage);

        if (isValidLanguage) {
            this.currentLanguage = detectedLanguage;
        } else {
            console.warn('Invalid language detected, falling back to default:', detectedLanguage);
            this.currentLanguage = LANGUAGES.SWISS_GERMAN;
        }

        debug('Current language set to:', this.currentLanguage);

        debug('IntroLayout initialized with language:', this.currentLanguage);

        // Use setTimeout to ensure the DOM is ready and change detection runs
        setTimeout(() => {
            this.cdr.detectChanges();
        }, 0);
    }

    /**
     * Extract language from URL path
     */
    private getCurrentLanguageFromUrl(): string | null {
        const currentUrl = this.router.url;
        debug('Parsing URL for language:', currentUrl);

        // Split URL and remove empty segments
        const urlSegments = currentUrl.split('/').filter((segment) => segment !== '');

        if (urlSegments.length > 0) {
            const firstSegment = urlSegments[0];
            // Remove query parameters and fragments if present
            const cleanSegment = firstSegment.split('?')[0].split('#')[0];

            debug('First URL segment (cleaned):', cleanSegment);

            // Check if the segment is a valid language code
            const availableLanguageCodes = Object.values(LANGUAGES);
            if (availableLanguageCodes.includes(cleanSegment as LANGUAGES)) {
                debug('Found valid language in URL:', cleanSegment);
                return cleanSegment;
            } else {
                debug('First segment is not a language code:', cleanSegment, 'Available:', availableLanguageCodes);
            }
        }

        debug('No language found in URL');
        return null;
    }

    /**
     * Change language and navigate to new URL
     */
    onLanguageChange(event: Event): void {
        const newLanguage = (event.target as HTMLSelectElement).value;

        if (newLanguage === this.currentLanguage) {
            return;
        }

        // Navigate to the same route but with new language
        // The page will reload, so i18next will automatically pick up the new language from the URL
        this.navigateToLanguageUrl(newLanguage);
    }

    /**
     * Handle language change from ngModel
     */
    onLanguageChangeModel(newLanguage: string): void {
        debug('Language changed via ngModel:', newLanguage, 'Current:', this.currentLanguage);

        // Don't check if newLanguage === this.currentLanguage because ngModel has already updated it
        // Navigate to the same route but with new language
        // The page will reload, so i18next will automatically pick up the new language from the URL
        this.navigateToLanguageUrl(newLanguage);
    }

    /**
     * Navigate back to login page while preserving the current language
     */
    onNavigateToLogin(): void {
        const currentLanguage = this.getCurrentLanguageFromUrl() || this.currentLanguage || LANGUAGES.SWISS_GERMAN;
        const loginPath = `/${currentLanguage}/auth/login`;

        debug('Navigating to login with Angular Router:', loginPath);

        // Navigate to login page with current language using Angular Router
        this.router.navigateByUrl(loginPath);
    }

    /**
     * Navigate to URL with specified language
     */
    private navigateToLanguageUrl(language: string): void {
        const currentUrl = this.router.url;
        const urlSegments = currentUrl.split('/').filter((segment) => segment !== ''); // Remove empty segments

        // Check if first segment is a language code
        const hasLanguageSegment =
            urlSegments.length > 0 && Object.values(LANGUAGES).includes(urlSegments[0] as LANGUAGES);

        if (hasLanguageSegment) {
            // Replace existing language segment
            urlSegments[0] = language;
        } else {
            // Insert language as first segment
            urlSegments.unshift(language);
        }

        // Construct new URL
        const newUrl = '/' + urlSegments.join('/');

        // Force page reload to ensure i18next picks up the new language
        // This is necessary because Angular doesn't reload components when navigating to the same route
        window.location.href = newUrl;
    }
}
